import React, { useState, useRef, useEffect } from 'react';
import { useMusic } from '../../context/MusicContext';
import { useMode } from '../../context/ModeContext';
import { 
  TerminalWrapper, TerminalBox, TerminalHeader, MacButton,
  TerminalContent, CommandLine, InputRow, 
  PromptString, TerminalInput,
  TapCommand, ProjectLink, OpenTerminalButton
} from './Hero.styles';
/* --- DYNAMIC TERMINAL TEXT COMPONENTS --- */
const TypewriterText = ({ text }) => {
  const [displayed, setDisplayed] = useState('');

  useEffect(() => {
    let i = 0;
    setDisplayed(''); // Reset when text changes
    const interval = setInterval(() => {
      setDisplayed(text.substring(0, i + 1));
      i++;
      if (i >= text.length) clearInterval(interval);
    }, 30); // 30ms per character typing speed
    
    return () => clearInterval(interval);
  }, [text]);

  return <span>{displayed}</span>;
};

const DynamicMusicOutput = () => {
  const { currentTrack } = useMusic();
  if (!currentTrack) return null;
  return <TypewriterText text={`now playing: "${currentTrack.title}" by ${currentTrack.artist}`} />;
};
const Hero = () => {
  const [input, setInput] = useState('');
  const [history, setHistory] = useState([]);
  
  const [isClosed, setIsClosed] = useState(false);
  const { isTerminalExpanded: isExpanded, setIsTerminalExpanded: setIsExpanded } = useMode();
  
  const hasInitialized = useRef(false);
  const inputRef = useRef(null);
  const terminalContentRef = useRef(null);
  const { executeMusicCommand } = useMusic(); 

  // 1. THE AUTOFOCUS FIX
  useEffect(() => {
    if (!isClosed && window.innerWidth > 768) {
      const focusTimer = setTimeout(() => {
        inputRef.current?.focus();
      }, 50);
      return () => clearTimeout(focusTimer);
    }
  }, [isClosed]);

  // 2. THE MINIMIZE SNAP FIX
  useEffect(() => {
    let scrollTimer;
    
    if (isExpanded) {
      document.body.style.overflow = 'hidden';
    } else {
      scrollTimer = setTimeout(() => {
        document.body.style.overflow = 'unset';
      }, 500); 
    }
    
    return () => {
      if (scrollTimer) clearTimeout(scrollTimer);
      document.body.style.overflow = 'unset'; 
    };
  }, [isExpanded]);

  useEffect(() => {
    if (!isClosed && !hasInitialized.current) {
       hasInitialized.current = true; 
       executeCommand('ls', false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isClosed]);

  useEffect(() => {
    if (terminalContentRef.current) {
      terminalContentRef.current.scrollTop = terminalContentRef.current.scrollHeight;
    }
  }, [history]);

  const executeCommand = (cmdStr, showCommandInput = true) => {
    const cmd = cmdStr.trim().toLowerCase();
    
    setHistory((prev) => {
      const newHistory = [...prev];
      
      if (showCommandInput) {
        newHistory.push({ type: 'input', text: cmd });
      }

      switch (cmd) {
        case 'ls':
          newHistory.push({ 
            type: 'output', 
            content: (
              <>
                hi! use the commands below or cmd+k for search bar:<br /><br />
                <TapCommand onClick={() => executeCommand('whoami')}>whoami</TapCommand> - displays a short bio<br />
                <TapCommand onClick={() => executeCommand('music')}>music</TapCommand> - launch music player<br />
                <TapCommand onClick={() => executeCommand('end music')}>end music</TapCommand> - close music player<br />
                <TapCommand onClick={() => executeCommand('projects')}>projects</TapCommand> - view technical projects<br />
                <TapCommand onClick={() => executeCommand('clear')}>clear</TapCommand> - clear terminal <br />
              </>
            )
          });
          break;
        case 'whoami':
          newHistory.push({ 
            type: 'output', 
            content: "hi, i'm a junior at umich studying computer engineering and exploring the overlap of ai, hardware, and business. i'm from boise, idaho so i naturally love the outdoors. you can see pictures on my art page!" 
          });
          break;
        case 'music':
          executeMusicCommand('music');
          newHistory.push({ type: 'output', content: <DynamicMusicOutput /> });
          break;
        case 'end music':
          executeMusicCommand('end music');
          newHistory.push({ type: 'output', content: 'terminating audio session.' });
          break;
        case 'projects':
          newHistory.push({ 
            type: 'output', 
            content: (
              <>
                select a project to view source code:<br /><br />
                <ProjectLink href="https://github.com/darin-noronha/motor_application" target="_blank">~/motor_control_application</ProjectLink>
                <ProjectLink href="https://github.com/darin-noronha/FPGA_Calculator" target="_blank">~/sequential_calculator</ProjectLink>
                <ProjectLink href="https://github.com/darin-noronha/portfolio-website" target="_blank">~/website_v1</ProjectLink>
              </>
            )
          });
          break;
        case 'clear':
          return []; 
        case '':
          break;
        default:
          newHistory.push({ 
            type: 'output', 
            content: `command not found: ${cmd}. type 'ls' to see available commands.` 
          });
      }
      return newHistory;
    });
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      executeCommand(input);
      setInput('');
    }
  };

  if (isClosed) {
    return (
      <TerminalWrapper id="home">
        <OpenTerminalButton 
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          onClick={() => {
            setIsClosed(false); 
            setIsExpanded(false); 
            setHistory([]); 
            setInput('');
            hasInitialized.current = false; 
          }}
        >
          &gt;_ open terminal
        </OpenTerminalButton>
      </TerminalWrapper>
    );
  }

  return (
    <TerminalWrapper id="home">
      <TerminalBox 
        $isExpanded={isExpanded}
        layout
        transition={{ type: 'spring', stiffness: 250, damping: 25 }}
        onClick={() => window.innerWidth > 768 && inputRef.current?.focus()}
      >
        <TerminalHeader>
          <MacButton color="#ff5f56" $clickable onClick={() => setIsClosed(true)} />
          <MacButton 
             color="#ffbd2e" 
             $clickable={isExpanded} 
             onClick={() => isExpanded && setIsExpanded(false)} 
          />
          <MacButton color="#27c93f" $clickable onClick={() => setIsExpanded(true)} />
        </TerminalHeader>

        <TerminalContent ref={terminalContentRef}>
          {history.map((line, i) => (
            <CommandLine key={i}>
              {line.type === 'input' && <PromptString>darin@umich-sys:~$</PromptString>}
              {line.content || line.text}
            </CommandLine>
          ))}
          
          <InputRow>
            <PromptString>darin@umich-sys:~$</PromptString>
            <TerminalInput 
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              autoCapitalize="none"
              autoComplete="off"
              spellCheck="false"
              autoCorrect="off"
            />
          </InputRow>
        </TerminalContent>
      </TerminalBox>
    </TerminalWrapper>
  );
};

export default Hero;